import { useCompanyUser, deleteCompanyUserApi } from "@/api/services/admin/company/user";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { TopPage } from "@/components/elements/TopPage";
import { UpdateCompanyUserForm } from "@/features/company/components/UpdateCompanyUserForm";
import { convertCompanyUserResToForm } from "@/features/company/utils/converts";
import { AppRoutes } from "@/routes/config";
import { isNumber } from "@/utils/number";
import { getDynamicRoute } from "@/utils/url";
import { Box, Stack } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { styled } from "@mui/system";
import { Button } from "@/components/elements/Button";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { useDialog } from "@/hooks/useDialog";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { useToast } from "@/hooks/useToast";
import { showError } from "@/utils/error";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useQueryClient } from "@tanstack/react-query";
import { IconDelete } from "@/assets/icons";

type TCompanyUserEditPageProps = {};

export type UpdateCompanyUserFormRef = {
  submit: () => Promise<void>;
};

export const CompanyUserEdit: FC<TCompanyUserEditPageProps> = () => {
  const formRef = useRef<UpdateCompanyUserFormRef>(null);
  const { id, userId } = useParams();
  const navigate = useNavigate();
  const { data: companyUserResult, isLoading, isError } = useCompanyUser();
  const companyUserData = companyUserResult?.data;
  const confirmDelete = useDialog();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    try {
      const agreeToDelete = await confirmDelete.show();
      if (!agreeToDelete) return;
      const rs = await deleteCompanyUserApi(Number(id), [Number(userId)]);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_USER_LIST] });
      setIsDeleting(true);
    } catch (error) {
      showError(error);
    }
  };

  useEffect(() => {
    if (isDeleting && id) {
      navigate(getDynamicRoute(AppRoutes.companyDetail, { id }));
    }
  }, [isDeleting, id, navigate]);

  useEffect(() => {
    if (!id || !isNumber(id) || !userId || !isNumber(userId)) {
      if (id && isNumber(id)) {
      } else {
      }
    }
  }, [id, userId, navigate]);

  useEffect(() => {
    if (!id || !isNumber(id)) {
      navigate(AppRoutes.companyDuringContractList, { replace: true });
      return;
    }
    if (!userId || !isNumber(userId)) {
      navigate(getDynamicRoute(AppRoutes.companyDetail, { id }), { replace: true });
      return;
    }
    if (isLoading) return;
    if (isError || !companyUserData) {
      navigate(getDynamicRoute(AppRoutes.companyDetail, { id }), { replace: true });
    }
  }, [id, userId, isLoading, isError, companyUserData, navigate]);

  if (isLoading || !companyUserData) return <LoadingPage />;

  return (
    <CompanyWrapper sx={{ mt: 1 }}>
      <Box sx={{ mb: 2.5 }}>
        <Stack gap={4}>
          <TopPage
            enablePageBack={false}
            title="顧客ユーザーの編集"
            description="顧客側画面にログインできるユーザーアカウントに関する情報を編集できます。"
            rightContent={
              <Button variant="danger" size="sm" startIcon={<IconDelete />} onClick={handleDelete}>
                削除
              </Button>
            }
          />
          <UpdateCompanyUserForm
            previousData={convertCompanyUserResToForm(companyUserData)}
            userData={{
              cognitoStatus: companyUserData?.cognitoStatus,
              activeStatus: companyUserData?.activeStatus,
            }}
            ref={formRef}
            forceDisableBlocker={isDeleting}
          />
          <ConfirmDeleteModal
            open={confirmDelete.open}
            onClose={confirmDelete.cancel}
            onOk={confirmDelete.confirm}
            message="企業ユーザーを削除してもよろしいですか？"
          />
        </Stack>
      </Box>
    </CompanyWrapper>
  );
};

const CompanyWrapper = styled(Stack)`
  width: 1198px;
  margin: 0 auto;
`;
